import { computed, inject, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { StreamMessage } from 'stream-chat-angular';
import { map } from 'rxjs';
import { Attachment, ReactionResponse, UserResponse } from 'stream-chat';
import { DateFormats, formatDateToString } from '@core';
import {
  MentionedUser,
  MessageGroup,
  MessageReaction,
  MessageViewModel,
  QuotedMessage,
  UnreadSeparatorState
} from '../../../../application/interfaces';
import { MessageContextStorage } from '../../../../application/context-storage';
import { STREAM_CHANNEL_TOKEN, StreamChannelProvider } from '@messages/infra';

export class MessageListViewService {
  private readonly channelProvider: StreamChannelProvider = inject(STREAM_CHANNEL_TOKEN);
  private readonly messageContextStorage: MessageContextStorage = inject(MessageContextStorage);

  private readonly initialUnreadState: UnreadSeparatorState | null =
    this.messageContextStorage.getUnreadSeparatorState();

  private readonly rawMessages: Signal<StreamMessage[] | undefined> = toSignal(
    this.channelProvider.activeChannelMessages$.pipe(
      map((messages: unknown[] | undefined) => (messages as StreamMessage[]) || [])
    )
  );

  readonly firstUnreadMessageId: Signal<string | null> = computed(() => {
    const messages: StreamMessage[] | undefined = this.rawMessages();
    const unreadState: UnreadSeparatorState | null = this.initialUnreadState;
    if (!messages?.length || !unreadState) return null;

    const lastReadIndex: number = messages.findIndex((msg: StreamMessage) => msg.id === unreadState.lastReadMessageId);
    if (lastReadIndex === -1 || lastReadIndex >= messages.length - 1) return null;

    return messages[lastReadIndex + 1]?.id || null;
  });

  readonly messageGroups: Signal<MessageGroup[]> = computed(() => {
    const messages: StreamMessage[] | undefined = this.rawMessages();
    if (!messages?.length) return [];

    const mapped: MessageViewModel[] = messages.map((msg: StreamMessage) => this.mapToViewModel(msg));
    return this.groupByDate(mapped);
  });

  readonly messageCount: Signal<number> = computed(() => this.rawMessages()?.length || 0);

  private mapToViewModel(message: StreamMessage): MessageViewModel {
    const user: UserResponse | null | undefined = message.user;
    const isDeleted: boolean = !!message.deleted_at || message.type === 'deleted';
    const attachments: Attachment[] = (message.attachments as Attachment[]) || [];
    const imageUrls: string[] = isDeleted
      ? []
      : attachments
          .filter((att: Attachment) => att.type === 'image' && att.image_url)
          .map((att: Attachment) => att.image_url as string);

    const reactions: MessageReaction[] = isDeleted ? [] : this.mapReactions(message);
    const quotedMessage: QuotedMessage | null = isDeleted ? null : this.mapQuotedMessage(message);
    const mentionedUsers: MentionedUser[] = isDeleted
      ? []
      : ((message.mentioned_users as UserResponse[]) || [])
          .filter((u: UserResponse) => u.id)
          .map((u: UserResponse) => ({
            id: u.id,
            name: (u.name as string) || u.id || ''
          }));

    return {
      id: message.id || '',
      text: isDeleted ? '' : (message.text as string) || '',
      userId: user?.id || '',
      userName: (user?.name as string) || user?.id || 'Użytkownik',
      userImage: (user?.image as string) || null,
      createdAt: message.created_at ? new Date(message.created_at as string | Date) : new Date(),
      imageUrls,
      reactions,
      quotedMessage,
      isDeleted,
      mentionedUsers
    };
  }

  private mapReactions(message: StreamMessage): MessageReaction[] {
    const reactionCounts: Record<string, number> = (message.reaction_counts as Record<string, number>) || {};
    const ownReactions: ReactionResponse[] = (message.own_reactions as ReactionResponse[]) || [];
    const latestReactions: ReactionResponse[] = (message.latest_reactions as ReactionResponse[]) || [];
    const ownReactionTypes: Set<string> = new Set(ownReactions.map((r: ReactionResponse) => r.type));

    return Object.entries(reactionCounts).map(([type, count]: [string, number]) => ({
      type,
      count,
      ownReaction: ownReactionTypes.has(type),
      users: latestReactions
        .filter((r: ReactionResponse) => r.type === type && r.user)
        .map((r: ReactionResponse) => ({
          id: r.user!.id,
          name: (r.user!.name as string) || r.user!.id || '',
          image: (r.user!.image as string) || null
        }))
    }));
  }

  private mapQuotedMessage(message: StreamMessage): QuotedMessage | null {
    const quoted: StreamMessage | undefined = message.quoted_message as StreamMessage | undefined;
    if (!quoted) return null;

    const quotedAttachments: Attachment[] = (quoted.attachments as Attachment[]) || [];
    const quotedImageUrl: string | null =
      quotedAttachments.find((att: Attachment) => att.type === 'image')?.image_url || null;

    return {
      id: quoted.id || '',
      text: (quoted.text as string) || '',
      userName: (quoted.user?.name as string) || quoted.user?.id || 'Użytkownik',
      imageUrl: quotedImageUrl
    };
  }

  private groupByDate(messages: MessageViewModel[]): MessageGroup[] {
    const groups: Map<string, MessageViewModel[]> = new Map();

    messages.forEach((message: MessageViewModel) => {
      const dateKey: string = this.formatDateKey(message.createdAt);
      const existing: MessageViewModel[] = groups.get(dateKey) || [];
      existing.push(message);
      groups.set(dateKey, existing);
    });

    return Array.from(groups.entries()).map(([date, msgs]: [string, MessageViewModel[]]) => ({
      date,
      messages: msgs
    }));
  }

  private formatDateKey(date: Date): string {
    const today: Date = new Date();
    const yesterday: Date = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const messageDate: Date = new Date(date);

    if (this.isSameDay(messageDate, today)) {
      return 'Dzisiaj';
    }
    if (this.isSameDay(messageDate, yesterday)) {
      return 'Wczoraj';
    }

    return formatDateToString(messageDate, DateFormats.DayMonthYearDot);
  }

  private isSameDay(date1: Date, date2: Date): boolean {
    return (
      date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear()
    );
  }
}
